import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Box, InputAdornment } from '@mui/material';
import { ReactComponent as Search } from 'assets/icons/Search.svg';
import TextField from 'modules/views/shared/TextField';
import Checkbox from 'modules/views/shared/Checkbox/Checkbox';

import { useStyles } from '../styles';
import { FilterState, TabIndex } from '../types';

interface Project {
  id: string;
  name: string;
}

interface FilterMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onFilterChange: (filterState: FilterState) => void;
  filterState?: FilterState;
  projects: Project[];
  activeTab?: number;
}

const MENU_ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};

const MENU_TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

const NO_RELATED_PROJECT_ID = '__no_related_project__';

export const FilterMenu: React.FC<FilterMenuProps> = ({
  anchorEl,
  onClose,
  onFilterChange,
  filterState,
  projects,
  activeTab,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [search, setSearch] = useState(filterState?.search || '');
  const [selectedOptions, setSelectedOptions] = useState<string[]>(filterState?.selectedOptions || []);
  const [noRelatedProject, setNoRelatedProject] = useState<boolean>(filterState?.noRelatedProject || false);

  useEffect(() => {
    setSearch(filterState?.search || '');
    setSelectedOptions(filterState?.selectedOptions || []);
    setNoRelatedProject(filterState?.noRelatedProject || false);
  }, [filterState]);

  // Remove duplicates by project id
  const uniqueProjects = projects.reduce((acc, project) => {
    if (!acc.find(p => p.id === project.id)) {
      acc.push(project);
    }
    return acc;
  }, [] as Project[]);

  const filteredProjects = uniqueProjects.filter(project =>
    project?.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value;
    setSearch(newSearch);
  };

  const handleNoRelatedProjectToggle = () => {
    const newNoRelatedProject = !noRelatedProject;
    setNoRelatedProject(newNoRelatedProject);

    // When "No Related Project" is selected, clear all other selections
    if (newNoRelatedProject) {
      setSelectedOptions([]);
      onFilterChange({ search, selectedOptions: [], noRelatedProject: true });
    } else {
      onFilterChange({ search, selectedOptions: [], noRelatedProject: false });
    }
  };

  const handleOptionToggle = (projectId: string) => {
    // When selecting a project, deselect "No Related Project"
    if (noRelatedProject) {
      setNoRelatedProject(false);
    }

    const newSelectedOptions = selectedOptions.includes(projectId)
      ? selectedOptions.filter(id => id !== projectId)
      : [...selectedOptions, projectId];

    setSelectedOptions(newSelectedOptions);
    onFilterChange({ search, selectedOptions: newSelectedOptions, noRelatedProject: false });
  };

  const handleSelectAll = () => {
    // When selecting all, deselect "No Related Project"
    if (noRelatedProject) {
      setNoRelatedProject(false);
    }

    if (selectedOptions.length === filteredProjects.length) {
      setSelectedOptions([]);
      onFilterChange({ search, selectedOptions: [], noRelatedProject: false });
    } else {
      const allProjectIds = filteredProjects.map(proj => proj.id);
      setSelectedOptions(allProjectIds);
      onFilterChange({ search, selectedOptions: allProjectIds, noRelatedProject: false });
    }
  };

  const handleClearAll = () => {
    setSelectedOptions([]);
    setNoRelatedProject(false);
    onFilterChange({ search, selectedOptions: [], noRelatedProject: false });
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      className={classes.filterMenu}
      anchorOrigin={MENU_ANCHOR_ORIGIN}
      transformOrigin={MENU_TRANSFORM_ORIGIN}
    >
      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          placeholder={t('myCertifications.filter.search')}
          value={search}
          testId="my-certifications-filter-search-input"
          onChange={handleSearchChange}
          isSearchBar={true}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box className={classes.filterHeader}>
        <Box className={classes.filterActions}>
          <Checkbox
            key="selectAll"
            checked={selectedOptions.length === filteredProjects.length}
            testId="my-certifications-filter-select-all"
            onChange={handleSelectAll}
            label={<span className={classes.filterOptionLabel}>{t('myCertifications.filter.selectAll')}</span>}
            styleClass={classes.filterOption}
          />

          <span
            className={classes.filterAction}
            data-testid="my-certifications-filter-clear-all"
            onClick={handleClearAll}
          >
            {t('myCertifications.filter.clearAll')}
          </span>
        </Box>
      </Box>
      <Box className={classes.filterOptions}>
        {/* Show "No Related Project" option only when NOT on Required tab */}
        {activeTab !== TabIndex.REQUIRED && (
          <Checkbox
            key={NO_RELATED_PROJECT_ID}
            checked={noRelatedProject}
            testId="my-certifications-filter-no-related-project"
            onChange={handleNoRelatedProjectToggle}
            label={<span className={classes.filterOptionLabel}>{t('myCertifications.filter.noRelatedProject')}</span>}
            styleClass={classes.filterOption}
          />
        )}

        {filteredProjects.length > 0 ? (
          filteredProjects.map(project => (
            <Checkbox
              key={project.id}
              checked={selectedOptions.includes(project.id)}
              testId={`my-certifications-filter-project-${project.id}`}
              onChange={() => handleOptionToggle(project.id)}
              label={<span className={classes.filterOptionLabel}>{project.name}</span>}
              styleClass={classes.filterOption}
            />
          ))
        ) : (
          <Box className={classes.emptySearchState} data-testid="my-certifications-filter-no-results">
            {t('myCertifications.filter.noResults')}
          </Box>
        )}
      </Box>
    </Menu>
  );
};
